/**
 * The small repeating pieces a list of things is built from.
 *
 * A row with a label and a trailing action, a definition list of facts, a strip
 * of metadata badges, and the inline link that cross-references another record.
 * Each had been written per screen, so the same row read at a different height
 * in each panel.
 *
 * @module components/ui/List
 */

import React, { type ReactNode } from "react";

import { Link } from "react-router";

import { Icon } from "./Icon";

const OVERLAY =
  "absolute inset-0 rounded-box focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary";

const TONE_CLASS = { default: "bg-base-200", inset: "bg-base-100", none: "" } as const;

export interface ListRowProps {
  /** Free content under the title and description, or the whole body when neither is set. */
  children?: ReactNode;
  /** The row's name, in the heading weight. */
  title?: ReactNode;
  /** A muted line under the title. */
  description?: ReactNode;
  /** Sits before the content: an icon or an avatar. */
  leading?: ReactNode;
  /**
   * Sits opposite the content: a badge, a button, a status. On a clickable
   * row it must not be interactive, since the whole row is already the control.
   */
  trailing?: ReactNode;
  /** Makes the row a button. A chevron marks it, centred on the row. */
  onClick?: () => void;
  /**
   * Makes the row a link: a router `Link` for an in-app path, an anchor for
   * anything else. Takes precedence over `onClick`.
   */
  href?: string;
  /** Names a clickable row where its text alone would not say where it goes. */
  "aria-label"?: string;
  /**
   * Makes the whole row open something while its content reads as itself: a
   * named control laid over the row, after the content. For a row whose body
   * carries status and facts that would be lost inside one control's name.
   * `href` makes it a link, otherwise `onClick` makes it a button.
   */
  action?: { label: string; href?: string; onClick?: () => void; "data-testid"?: string };
  /**
   * `inset` for a row on a panel, matching the panel's own inset tone.
   * `none` paints no ground, for a caller that supplies its own.
   */
  tone?: "default" | "inset" | "none";
  /** `false` drops the border and corners, for a row inside a divided list. */
  framed?: boolean;
  className?: string;
  "data-testid"?: string;
}

/** The control laid over a row with an `action`: a link for `href`, a button otherwise. */
function RowAction({
  action,
}: {
  action: NonNullable<ListRowProps["action"]>;
}): React.ReactElement {
  if (action.href !== undefined) {
    return (
      <Link
        to={action.href}
        className={OVERLAY}
        aria-label={action.label}
        data-testid={action["data-testid"]}
      />
    );
  }
  return (
    <button
      type="button"
      className={OVERLAY}
      aria-label={action.label}
      onClick={action.onClick}
      data-testid={action["data-testid"]}
    />
  );
}

/**
 * One row of a list, with its trailing content pinned to the top right.
 *
 * It stays in the corner at every width and the content column shrinks
 * instead: wrapped, `justify-between` put an open action at the left edge,
 * stranded under the row it belonged to.
 */
export function ListRow({
  children = undefined,
  title = undefined,
  description = undefined,
  leading = undefined,
  trailing = undefined,
  onClick = undefined,
  href = undefined,
  "aria-label": ariaLabel = undefined,
  action = undefined,
  tone = "inset",
  framed = true,
  className = "",
  "data-testid": testId,
}: ListRowProps): React.ReactElement {
  const clickable = href !== undefined || onClick !== undefined || action !== undefined;
  const shell = [
    "flex items-start justify-between gap-3 px-4 py-3",
    framed ? "rounded-box border border-base-300" : "",
    TONE_CLASS[tone],
    clickable ? "w-full text-start hover:bg-surface-hover" : "",
    action ? "relative transition-colors" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const body = (
    <>
      {leading ? <span className="shrink-0">{leading}</span> : null}
      <span className="block min-w-0 flex-1">
        {title ? (
          <span className="block text-sm font-semibold text-base-content">{title}</span>
        ) : null}
        {description ? <span className="block text-sm text-muted">{description}</span> : null}
        {children}
      </span>
      {trailing ? <span className="flex shrink-0 items-center gap-2">{trailing}</span> : null}
      {clickable ? (
        <Icon name="caret-right" className="shrink-0 self-center text-muted" aria-hidden />
      ) : null}
    </>
  );
  if (href !== undefined) {
    return href.startsWith("/") ? (
      <Link to={href} className={shell} aria-label={ariaLabel} data-testid={testId}>
        {body}
      </Link>
    ) : (
      <a href={href} className={shell} aria-label={ariaLabel} data-testid={testId}>
        {body}
      </a>
    );
  }
  if (onClick !== undefined) {
    return (
      <button
        type="button"
        className={shell}
        onClick={onClick}
        aria-label={ariaLabel}
        data-testid={testId}
      >
        {body}
      </button>
    );
  }
  return (
    <div className={shell} data-testid={testId}>
      {body}
      {action ? <RowAction action={action} /> : null}
    </div>
  );
}

export interface KeyValueEntry {
  label: string;
  value: ReactNode;
  /** Lands on the value, which is what a test reads. */
  "data-testid"?: string;
}

export interface KeyValueProps {
  entries: readonly KeyValueEntry[];
  /** `inline` puts label and value on one line; `stacked` puts the value under. */
  layout?: "inline" | "stacked";
  /** `prose` is for sentences and nested structures; `figure` (the default) is for short facts. */
  valueWeight?: "figure" | "prose";
  /** Columns of a stacked figure list from `sm` up; one is a plain column of facts. Prose is always one. */
  columns?: 1 | 2 | 3;
  className?: string;
  "data-testid"?: string;
}

/** Written out, not interpolated: Tailwind only generates classes it can read. */
const KEY_VALUE_COLUMNS = {
  1: "",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 md:grid-cols-3",
} as const;

/** Short facts sit in columns; prose takes a full-width row each. */
function stackedClass(
  valueWeight: "figure" | "prose",
  columns: keyof typeof KEY_VALUE_COLUMNS,
): string {
  return valueWeight === "prose"
    ? "grid grid-cols-1 gap-3 divide-y divide-base-300 border-y border-base-300 [&>div]:pt-3 [&>div:first-child]:pt-0"
    : `grid gap-3 ${KEY_VALUE_COLUMNS[columns]}`.trim();
}

/**
 * Facts about one record, as a real definition list.
 *
 * The label is muted and the value carries the weight, because the value is
 * what is being read; a label in the same weight competes with it.
 */
export function KeyValue({
  entries,
  layout = "stacked",
  valueWeight = "figure",
  columns = 2,
  className = "",
  "data-testid": testId,
}: KeyValueProps): React.ReactElement {
  return (
    <dl
      className={[
        layout === "inline" ? "flex flex-wrap gap-x-6 gap-y-2" : stackedClass(valueWeight, columns),
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      data-testid={testId}
    >
      {entries.map((entry) => (
        <div
          key={entry.label}
          className={layout === "inline" ? "flex items-baseline gap-2" : "min-w-0"}
        >
          <dt className="text-xs font-bold tracking-[0.09em] text-muted uppercase">
            {entry.label}
          </dt>
          <dd
            className={`break-words text-sm text-base-content ${valueWeight === "figure" ? "font-semibold" : ""}`.trim()}
            data-testid={entry["data-testid"]}
          >
            {entry.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export interface MetaBadgeRowProps {
  children: ReactNode;
  className?: string;
  "data-testid"?: string;
}

/**
 * The strip naming a record: award type, level, credit volume, who owns it.
 *
 * Caption-sized and muted, so plain text may be passed as well as badges: at
 * body weight the strip reads as loud as the title above it, which is the one
 * thing in the row that has to carry. A `Badge` child brings its own colour
 * and size and is unaffected.
 */
export function MetaBadgeRow({
  children,
  className = "",
  "data-testid": testId,
}: MetaBadgeRowProps): React.ReactElement {
  return (
    <div
      className={`flex flex-wrap items-center gap-2 text-xs text-muted ${className}`.trim()}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

export interface LinkChipProps {
  to: string;
  children: ReactNode;
  /** Says where it goes, where the label alone is a bare code like "PLO 2". */
  title?: string;
  className?: string;
  "data-testid"?: string;
}

/** An inline cross-reference to another record, sized to sit inside a sentence. */
export function LinkChip({
  to,
  children,
  title = undefined,
  className = "",
  "data-testid": testId,
}: LinkChipProps): React.ReactElement {
  return (
    <Link
      to={to}
      title={title}
      className={`link link-hover text-xs font-medium ${className}`.trim()}
      data-testid={testId}
    >
      {children}
    </Link>
  );
}
